import merge from "deepmerge";
import React, { useMemo } from "react";
import { Global, css } from "@emotion/react";
import { copyObject, dotGet } from "../../utils/utils";
import { Colors, ColorSchemes, IThemeColor, IThemeColors } from "../colors";
import { IColorsSet, ITheme, Theme } from "../index";
import { ThemeContext } from "../ThemeContext";

const applyColors = ({
  colorsSet,
  colors,
}: {
  colorsSet: IColorsSet;
  colors: IThemeColor;
}): IColorsSet => {
  const keys = Object.keys(colorsSet);

  for (let i = 0; i < keys.length; i += 1) {
    if (colorsSet[keys[i]]) {
      colorsSet[keys[i]] = dotGet(colors, `${colorsSet[keys[i]]}.color`);
    }
  }

  return colorsSet;
};

type Props = {
  colorScheme?: ColorSchemes;
  colors?: IThemeColors;
  theme?: ITheme;
};

const AppThemeProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  colorScheme = ColorSchemes.LIGHT,
  theme: customTheme,
  colors: customColors,
}) => {
  const colors = useMemo<IThemeColor>(() => {
    let current = Colors[colorScheme];
    if (customColors) {
      current = merge(current, customColors[colorScheme]);
    }

    return current;
  }, [colorScheme, customColors]);

  const theme = useMemo<ITheme>(() => {
    let current: ITheme = copyObject(Theme);

    if (customTheme) {
      current = merge(current, customTheme);
    }

    applyColors({ colorsSet: current.fonts.roles.base.colors, colors });
    applyColors({ colorsSet: current.fonts.roles.primary.colors, colors });
    applyColors({ colorsSet: current.fonts.roles.secondary.colors, colors });
    applyColors({ colorsSet: current.fonts.roles.tertiary.colors, colors });
    applyColors({ colorsSet: current.fonts.roles.inverted.colors, colors });

    applyColors({ colorsSet: current.buttons.roles.primary.colors, colors });
    applyColors({ colorsSet: current.buttons.roles.secondary.colors, colors });
    applyColors({ colorsSet: current.buttons.roles.neutral.colors, colors });
    applyColors({ colorsSet: current.buttons.roles.tertiary.colors, colors });

    applyColors({
      colorsSet: current.linearProgress.roles.primary.colors,
      colors,
    });
    applyColors({
      colorsSet: current.linearProgress.roles.secondary.colors,
      colors,
    });
    applyColors({
      colorsSet: current.linearProgress.roles.tertiary.colors,
      colors,
    });

    applyColors({ colorsSet: current.links.roles.primary.colors, colors });
    applyColors({ colorsSet: current.links.roles.secondary.colors, colors });
    applyColors({ colorsSet: current.links.roles.neutral.colors, colors });
    applyColors({ colorsSet: current.links.roles.tertiary.colors, colors });

    applyColors({ colorsSet: current.slider.colors.text, colors });
    applyColors({ colorsSet: current.slider.colors.content, colors });

    applyColors({ colorsSet: current.inputs.colors.font, colors });
    applyColors({ colorsSet: current.inputs.colors.background, colors });
    applyColors({ colorsSet: current.inputs.colors.border, colors });

    applyColors({ colorsSet: current.search.colors.text, colors });
    applyColors({ colorsSet: current.search.colors.background, colors });
    applyColors({ colorsSet: current.search.colors.border, colors });
    applyColors({ colorsSet: current.search.colors.icon, colors });

    applyColors({ colorsSet: current.table.colors.header, colors });
    applyColors({ colorsSet: current.table.colors.border, colors });

    applyColors({
      colorsSet: current.checkboxes.states.primary.colors,
      colors,
    });
    applyColors({
      colorsSet: current.checkboxes.states.neutral.colors,
      colors,
    });
    applyColors({
      colorsSet: current.checkboxes.states.secondary.colors,
      colors,
    });
    applyColors({
      colorsSet: current.checkboxes.states.tertiary.colors,
      colors,
    });

    applyColors({ colorsSet: current.radios.states.colors, colors });
    applyColors({ colorsSet: current.toggle.thumb.colors, colors });
    applyColors({ colorsSet: current.toggle.track.colors, colors });

    applyColors({ colorsSet: current.tags.roles.neutral.colors, colors });
    applyColors({ colorsSet: current.tags.roles.info.colors, colors });
    applyColors({ colorsSet: current.tags.roles.warning.colors, colors });
    applyColors({ colorsSet: current.tags.roles.error.colors, colors });
    applyColors({ colorsSet: current.tags.roles.success.colors, colors });

    return current;
  }, [colors, customTheme]);

  const currentTheme = useMemo(() => {
    return { colorScheme, theme, colors };
  }, [theme, colors, colorScheme]);

  return (
    <>
      <Global
        styles={css`
          @import url("https://fonts.cdnfonts.com/css/roobert");
          @import url("https://fonts.cdnfonts.com/css/sf-pro-display");
        `}
      />
      <ThemeContext.Provider value={currentTheme}>
        {children}
      </ThemeContext.Provider>
    </>
  );
};

export { AppThemeProvider };
